import React, { useState, useEffect } from "react";
import { User } from "../types/User";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/UserFormulaireAdmin.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useNavigate } from "react-router-dom";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import BackButton from "../components/BackButton";
import AuthentifiedPage from "./AuthentifiedPage";
import { userService } from "../services/UserService";
import { roleService } from "../services/RoleService";
import { Role } from "../types/Role";
import { useAuth } from "../context/AuthContext";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";

const UserFormulaireAdmin: React.FC = () => {
  const [userForm, setUserForm] = useState<Partial<User>>({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    username: "",
    password: "",
    address: "",
    role: "",
  });

  const [successVisible, setSuccessVisible] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const navigate = useNavigate();
  const { api, user } = useAuth();

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolesData = await roleService.getAllRoles(api);
        setRoles(rolesData);
      } catch (error) {
        console.error('Erreur lors du chargement des rôles:', error);
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, [api]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const genererMotDePasse = () => {
    const pwd = Math.random().toString(36).slice(-8);
    setUserForm((prev) => ({ ...prev, password: pwd }));
    alert(`Mot de passe généré: ${pwd}`);
  };

  const handleCreer = async () => {
    if (!userForm.nom || !userForm.username || !userForm.password) {
      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 2000);
      return;
    }

    try {
      setLoadingCreate(true);
      const roleId = Number(userForm.role) || 3;

      const newUser = {
        firstName: userForm.prenom || "",
        lastName: userForm.nom,
        email: userForm.email || "",
        phoneNumber: userForm.telephone || "",
        username: userForm.username,
        password: userForm.password,
        address: userForm.address,
        role: { roleId: roleId },
      };

      await userService.createUser(api, newUser, user?.email);            

      setSuccessVisible(true);
      setTimeout(() => {
        setSuccessVisible(false);
        navigate("/admin");
      }, 1500);
    } catch (error: any) {
      console.error("Erreur lors de la création de l'utilisateur :", error);

      switch (error.message) {
        case "EMAIL_TAKEN":
          setErrorMessage("Cet email est déjà utilisé.");
          break;
        case "USERNAME_TAKEN":
          setErrorMessage("Ce nom d'utilisateur est déjà utilisé.");
          break;
        case "SERVER_ERROR":
          setErrorMessage("Erreur serveur. Veuillez réessayer plus tard.");
          break;
        default:
          setErrorMessage("Une erreur inattendue est survenue.");
      }

      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 3000);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleAnnuler = () => {
    setUserForm({
      nom: "",
      prenom: "",
      email: "",
      telephone: "",
      username: "",
      password: "",
      address: "",
      role: "",
    });
  };

  return (
      <AuthentifiedPage>
        {/* Messages de succès / erreur */}
      <CollecteSuccessMessage
        visible={successVisible}
        message="Utilisateur créé avec succès"
      />
      <CollecteErrorMessage
        visible={errorVisible}
        message={errorMessage}
      />

      <main className="container py-4 flex-grow-1">
        <BackButton text="Retour" />
        <div className="user-header mb-4">
          <h5 className="text-success fw-bold">Fiche de création d’un utilisateur</h5>
          <p className="text-muted small mb-0">
            Complétez les informations ci-dessous pour enregistrer un nouvel utilisateur dans le système.
          </p>
        </div>

        <div className="bg-white rounded shadow-sm p-4">
          <h6 className="fw-semibold mb-3 text-dark">Informations sur l’utilisateur</h6>
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <input
                type="text"
                name="nom"
                value={userForm.nom}
                onChange={handleChange}
                className="form-control"
                placeholder="Nom"
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="prenom"
                value={userForm.prenom}
                onChange={handleChange}
                className="form-control"
                placeholder="Prénoms"
              />
            </div>
            <div className="col-md-6">
              <input
                type="email"
                name="email"
                value={userForm.email}
                onChange={handleChange}
                className="form-control"
                placeholder="Email"
              />
            </div>
            <div className="col-md-6">
              <input
                type="text"
                name="telephone"
                value={userForm.telephone}
                onChange={handleChange}
                className="form-control"
                placeholder="Téléphone"
              />
            </div>
            <div className="col-md-6">
              <input 
                type="text" 
                name="address" 
                value={userForm.address} 
                onChange={handleChange}
                className="form-control" 
                placeholder="Adresse" />
            </div>
          </div>

          <h6 className="fw-semibold mb-3 text-dark">Authentification</h6>
          <div className="row g-3 align-items-center mb-4">
            <div className="col-md-4">
              <input
                type="text"
                name="username"
                value={userForm.username}
                onChange={handleChange}
                className="form-control"
                placeholder="Nom d'utilisateur"
              />
            </div>
            <div className="col-md-4">
              <input
                type="password"
                name="password"
                value={userForm.password}
                onChange={handleChange}
                className="form-control"
                placeholder="Mot de passe"
              />
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-success w-100"
                type="button"
                onClick={genererMotDePasse}
              >
                Générer le mot de passe
              </button>
            </div>
          </div>

          <h6 className="fw-semibold mb-3 text-dark">Rôles</h6>
          <div className="col-md-6 mb-3">
            <select
              className="form-select"
              name="role"
              value={userForm.role}
              onChange={handleChange}
              disabled={loadingRoles}
            >
              <option value="">Séléctionner un rôle</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          
          {loadingCreate && <CollecteLoadingMessage message="Envoi d'email et insertion de l'utilisateur..." />}
        </div>
      </main>
        <Footer
        leftButtons={[
            {
            text: "Annuler",
            color: "secondary",
            onClick: handleAnnuler,
            },
        ]}
        rightButton={{
            text: "Créer et enregistrer un utilisateur",
            color: "green",
            onClick: handleCreer,
        }}
        />
      </AuthentifiedPage>
  );
};

export default UserFormulaireAdmin;
